import {
  collectHeadings,
  createHeadingNumberingState,
  nextHeadingNumbering,
  type THeadingNumberingState,
} from '@confinion/confideck-shared';
import type { Node as PMNode } from '@tiptap/pm/model';

import type { THeading } from '../../../types';

/** The heading node's name. Kept here so the numbering has no dependency on the node view. */
export const HEADING_NODE_NAME = 'heading';

export type TTreeHeading = THeading & {
  children: TTreeHeading[];
};

export type THeadingNumberingPatch = { pos: number; attrs: Record<string, unknown>; numbering: string };

/** Counter state, so numbering can be resumed part-way through a document. */
export type THeadingState = THeadingNumberingState;

export type THeadingNumbering = {
  /** The outline, nested by level. */
  tree: TTreeHeading[];
  /** Headings whose `numbering` attribute is out of date. Empty when nothing changed. */
  patches: THeadingNumberingPatch[];
  count: number;
  /** Counter state after the last heading - the seed for whatever follows this doc. */
  state: THeadingState;
};

/**
 * Number every heading in a document and build the outline.
 *
 * Pure and doc-based rather than state-based, because it has two callers: this plugin, over the
 * editor's document, and the sections model, over the WHOLE document. Under sectioning the editor
 * holds one section, so an outline built from it would list only that section and the numbering
 * would restart at 1 in the middle of the document. One implementation keeps them from drifting.
 */
export const numberHeadings = (doc: PMNode, headingStart: number, initial?: THeadingState): THeadingNumbering => {
  const state = initial
    ? { counters: initial.counters.slice(), isFirst: initial.isFirst }
    : createHeadingNumberingState();
  const tree: TTreeHeading[] = [];
  const patches: THeadingNumberingPatch[] = [];
  let count = 0;

  collectHeadings(doc).forEach(({ node, pos }) => {
    const id = node.attrs.id;
    const level0Based = node.attrs.level - 1;
    const numbering = nextHeadingNumbering(state, node.attrs.level, headingStart);

    if (node.attrs.numbering !== numbering) patches.push({ pos, attrs: { ...node.attrs, id }, numbering });

    let currentHeadings = tree;
    let currentLevel0Based = 0;
    let currentHeading;
    while (currentHeadings.length > 0 && currentLevel0Based < level0Based) {
      currentHeading = currentHeadings[currentHeadings.length - 1];
      if (currentHeading.level - 1 >= level0Based) break;
      currentHeadings = currentHeading.children;
      currentLevel0Based = currentHeading.level - 1;
    }
    currentHeadings.push({ id, level: node.attrs.level, text: node.textContent.trim(), numbering, children: [] });

    count++;
  });

  return { tree, patches, count, state };
};
